import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import logo from '../../assets/image/logo/logo.svg';
import './header.scss';

function Header() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { toggleTheme, isDark } = useTheme();
    
    const [menuOpen, setMenuOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [currentLang, setCurrentLang] = useState(
        i18n.language ? i18n.language.split('-')[0] : 'ru'
    );
    
    const langRef = useRef(null);
    const burgerRef = useRef(null);
    const mobileMenuRef = useRef(null);

    const languages = useMemo(() => [
        { code: 'ru', label: 'RU' },
        { code: 'en', label: 'EN' },
        { code: 'uz', label: 'UZ' }
    ], []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (langRef.current && !langRef.current.contains(event.target)) {
                setLangOpen(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && 
                burgerRef.current && !burgerRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [menuOpen]);

    useEffect(() => {
        const onLangChanged = (lng) => {
            setCurrentLang(lng.split('-')[0]);
        };
        
        i18n.on('languageChanged', onLangChanged);
        return () => {
            i18n.off('languageChanged', onLangChanged);
        };
    }, [i18n]);

    useEffect(() => {
        const handleScroll = () => {
            const header = document.querySelector('.header__container');
            if (header) {
                if (window.scrollY > 50) {
                    header.classList.add('scrolled');
                } else {
                    header.classList.remove('scrolled');
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const current = languages.find(l => l.code === currentLang) || languages[0];

    const changeLanguage = useCallback((lng) => {
        i18n.changeLanguage(lng);
        setCurrentLang(lng);
        setLangOpen(false);
    }, [i18n]);

    const toggleLanguageMenu = useCallback((e) => {
        e.stopPropagation();
        setLangOpen(prev => !prev);
    }, []);

    const toggleMobileMenu = useCallback(() => {
        setMenuOpen(prev => !prev);
        if (!menuOpen) {
            setLangOpen(false);
        }
    }, [menuOpen]);

    const closeMobileMenu = useCallback(() => {
        setMenuOpen(false);
    }, []);

    const handleEducationClick = useCallback((e) => {
        closeMobileMenu();
        window.location.href = 'https://academy.mmxhamid.uz';
    }, [closeMobileMenu]);

    const handleLegalClick = useCallback((e) => {
        closeMobileMenu();
        navigate('/legal');
    }, [navigate, closeMobileMenu]);

    return (
        <div className="header__container">
            <div className="header__wrapper">
                <div className="header__content">
                    <div className="header__logo">
                        <Link to="/" onClick={closeMobileMenu}>
                            <img src={logo} alt="logo" />
                        </Link>
                    </div>
                    <nav className="header__nav">
                        <ul>
                            {['home', 'about', 'reviews', 'contact'].map((item) => (
                                <li key={item}>
                                    <a 
                                        href={`#${item}`}
                                        className="header__nav-link"
                                        onClick={(e) => {
                                            if (window.location.pathname !== '/') {
                                                e.preventDefault();
                                                navigate('/');
                                                setTimeout(() => {
                                                    const element = document.getElementById(item);
                                                    if (element) {
                                                        element.scrollIntoView({ behavior: 'smooth' });
                                                    }
                                                }, 100);
                                            }
                                        }}
                                    >
                                        <span className="header__nav-text">{t(item)}</span>
                                        <span className="header__nav-underline" />
                                    </a>
                                </li>
                            ))}
                            <li>
                                <button 
                                    onClick={handleEducationClick}
                                    className="header__nav-link header__nav-button"
                                >
                                    <span className="header__nav-text">
                                        {t('Education') || 'Обучение'}
                                    </span>
                                    <span className="header__nav-underline" />
                                </button>
                            </li>
                            <li>
                                <button 
                                    onClick={handleLegalClick}
                                    className="header__nav-link header__nav-button"
                                >
                                    <span className="header__nav-text">
                                        {t('legal') || 'Юридическая поддержка'}
                                    </span>
                                    <span className="header__nav-underline" />
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>

                <div className="header__right">
                    <button 
                        className="header__theme-btn"
                        onClick={toggleTheme}
                        aria-label="Toggle theme"
                    >
                        {isDark ? '☀️' : '🌙'}
                    </button>

                    <div 
                        className="header__language-select" 
                        ref={langRef}
                    >
                        <button
                            className={`header__language-btn ${langOpen ? 'open' : ''}`}
                            onClick={toggleLanguageMenu}
                            type="button"
                            aria-label="Change language"
                            aria-expanded={langOpen}
                        >
                            {current.label}
                        </button>
                        <ul className={`header__language-list ${langOpen ? 'open' : ''}`}>
                            {languages.map(l => (
                                <li key={l.code}>
                                    <button 
                                        onClick={() => changeLanguage(l.code)}
                                        className={l.code === currentLang ? 'active' : ''}
                                        aria-label={`Change to ${l.label}`}
                                    >
                                        {l.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <button 
                        className={`header__burger ${menuOpen ? 'active' : ''}`}
                        ref={burgerRef}
                        onClick={toggleMobileMenu}
                        aria-label="Menu"
                        aria-expanded={menuOpen}
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                </div>
                
                <div 
                    className={`header__mobile-menu ${menuOpen ? 'open' : ''}`}
                    ref={mobileMenuRef}
                    aria-hidden={!menuOpen}
                >
                    <ul>
                        {['home', 'about', 'reviews', 'contact'].map((item) => (
                            <li key={item}>
                                <a 
                                    href={`#${item}`} 
                                    onClick={(e) => {
                                        if (window.location.pathname !== '/') {
                                            e.preventDefault();
                                            navigate('/');
                                            setTimeout(() => {
                                                const element = document.getElementById(item);
                                                if (element) {
                                                    element.scrollIntoView({ behavior: 'smooth' });
                                                }
                                            }, 100);
                                        }
                                        closeMobileMenu();
                                    }}
                                >
                                    {t(item)}
                                </a>
                            </li>
                        ))}
                        <li>
                            <button onClick={handleEducationClick} className="mobile-nav-button">
                                {t('Education') || 'Обучение'}
                            </button>
                        </li>
                        <li>
                            <button onClick={handleLegalClick} className="mobile-nav-button">
                                {t('legal') || 'Юридическая поддержка'}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default Header;